import { useState } from "react";
import { useAuth } from "@clerk/react";
import { Instagram } from "lucide-react";
import { apiFetch } from "@/lib/reels";
import { PrimaryButton } from "@/components/reels/controls";

const CODE_HINT = {
  "2fa": "Enter the code from your authenticator app or the SMS Instagram sent.",
  challenge: "Instagram sent a security code to your email or phone. Enter it here.",
};

export const ConnectInstagram = ({ onConnected }) => {
  const { getToken } = useAuth();
  const [form, setForm] = useState({ username: "", password: "", code: "" });
  const [step, setStep] = useState("creds");
  const [kind, setKind] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handle = async (path, body, method = "POST") => {
    setBusy(true);
    setError("");
    try {
      let data = await (await apiFetch(path, { method, token: await getToken(), body: body && JSON.stringify(body) })).json();
      while (data.status === "running") {
        data = await (await apiFetch("/instagram/connect/pending", { token: await getToken() })).json();
      }
      if (data.status === "need_code") {
        setKind(data.kind);
        setStep("code");
      } else if (data.status === "connected") {
        onConnected();
      }
    } catch (e) {
      setError(e.message);
      if (step === "code") setStep("creds");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="rs-material flex flex-col gap-3.5 p-4 rs-fade" data-testid="ig-connect">
      <div className="flex items-center gap-2">
        <Instagram size={20} />
        <p className="text-[17px] font-semibold">Link Instagram</p>
      </div>
      {step === "creds" ? (
        <>
          <input className="rs-field rs-input" placeholder="Instagram username" value={form.username} onChange={set("username")}
            autoCapitalize="none" autoCorrect="off" data-testid="ig-username-input" />
          <input className="rs-field rs-input" placeholder="Password" type="password" value={form.password} onChange={set("password")}
            data-testid="ig-password-input" />
          <PrimaryButton testId="ig-connect-btn" busy={busy} disabled={busy || !form.username || !form.password}
            onClick={() => handle("/instagram/connect", { username: form.username, password: form.password })}
            label={busy ? "Connecting…" : "Connect Instagram"} />
        </>
      ) : (
        <>
          <p className="rs-secondary text-[14px]" data-testid="ig-code-hint">{CODE_HINT[kind] || CODE_HINT.challenge}</p>
          <input className="rs-field rs-input tracking-widest" placeholder="123456" inputMode="numeric" value={form.code}
            onChange={set("code")} data-testid="ig-code-input" />
          <PrimaryButton testId="ig-code-submit-btn" busy={busy} disabled={busy || !form.code}
            onClick={() => handle("/instagram/connect/code", { code: form.code })} label={busy ? "Verifying…" : "Verify"} />
        </>
      )}
      {error && <p className="text-[13px] text-[#c62d22]" data-testid="ig-connect-error">{error}</p>}
      <p className="rs-secondary text-[12px]">
        Your password is only used once to sign in and is never stored. Recipe Remedy keeps an encrypted Instagram session so you can browse your Saved folders. Instagram may ask you to approve this login.
      </p>
    </div>
  );
};
